import { componentHasDefinedNominal } from 'features/assetModeling'
import { createBinnedDataBufferFromData } from 'features/dataLayers/geckoBinnedDataBuffer'
import { createCdfEntry } from 'features/dataLayers/generation/cdfGenerator'
import NominalWorker from 'features/dataLayers/generation/coreLayers/nominal/calculateNominalValues.worker?worker'
import { useBinnedDataBufferCache } from 'features/dataLayers/load/useBinnedDataBufferCache'
import { getInspectionDate } from 'features/fde'
import { isNil, memoize, uniq } from 'lodash'
import { useCallback, useMemo } from 'react'
import {
  ForecastComponentConfig,
  GeckoAggregation,
  GeckoAlgorithm,
  GeckoAnglePlotSetting,
  GeckoAssetModel,
  GeckoBinSize,
  GeckoComponentCoords,
  GeckoComponentDataFrame,
  GeckoComponentDataset,
  GeckoComponentModel,
  GeckoComponentModelType,
  GeckoComponentRect,
  GeckoInspection,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  GeckoSource,
  GeckoView,
  RolodexEntryType,
} from 'types'
import { formatDateLikeInspectionData } from 'utils/dateHelpers'
import { snapToNearestMultiple, SnapType } from 'utils/math'

import { NominalCalculationProps } from './calculateNominalValues.worker'

const NOMINAL_MEASUREMENT_UNIT = GeckoMeasurementUnit.Millimeters
export type NominalBaseData = {
  // The date to show the nominal values for (ignoring all repairs after this date)
  nominalDate: Date
  // The bin size to use for the nominal CDF
  binSize: GeckoBinSize
  // The tags to apply to the nominal CDF
  cdfEntryTags: GeckoComponentDataFrame['tags']
  // The generation number to use (a function applied to the generation numbers of all entries used to create this BaseData)
  generationNumber: number
}

export function useGenerateNominalCDFs(
  assetModel: GeckoAssetModel | undefined,
  componentId: string | undefined,
  baseData: ReadonlyArray<NominalBaseData> | undefined,
  forecastComponentConfig: ForecastComponentConfig | undefined
): ReadonlyArray<GeckoComponentDataFrame> {
  // ------------------------------------------------------------------
  // Create the function to spin up and call the web-worker that will calculate the nominal values
  const calculateNominalValuesInWorker = useCallback(
    (props: {
      nominalDate: Date
      outputDataXSize: number
      outputDataYSize: number
      binSize: GeckoBinSize
      geometryXStart: GeckoMeasurement
      geometryYStart: GeckoMeasurement
    }) =>
      Promise.resolve(
        new Promise<{
          nominalDate: Date
          binSizeUsed: GeckoBinSize
          values: Float32Array
        }>((resolve, reject) => {
          const {
            nominalDate,
            outputDataXSize,
            outputDataYSize,
            binSize,
            geometryXStart,
            geometryYStart,
          } = props

          const inspectionDateString = formatDateLikeInspectionData(nominalDate)

          if (
            isNil(assetModel) ||
            isNil(componentId) ||
            !inspectionDateString
          ) {
            reject('Invalid input data, something needed is not defined')
            return
          }

          const nominalCalculationProps: NominalCalculationProps = {
            componentId,
            assetModelJson: assetModel.raw,
            outputUnitId: NOMINAL_MEASUREMENT_UNIT.id,
            xBinCount: outputDataXSize,
            yBinCount: outputDataYSize,
            binHeightJson: binSize.height.toJSON(),
            binWidthJson: binSize.width.toJSON(),
            yGeometryStartJson: geometryYStart.toJSON(),
            xGeometryStartJson: geometryXStart.toJSON(),
            lastDateToApplyNominalsFromStr: inspectionDateString,
            forecastComponentConfig,
          }

          const nominalWorker = new NominalWorker()

          // The function that will be called when the web-worker posts a message
          nominalWorker.onmessage = async (message) => {
            nominalWorker.terminate()
            resolve({
              nominalDate: nominalDate,
              binSizeUsed: binSize,
              values: new Float32Array(message.data as number[]),
            })
          }

          // The function that will be called when the web-worker posts an error
          nominalWorker.onerror = async (error) => {
            console.error(error)
            nominalWorker.terminate()
            reject(`Couldn't calculate derived CDF, ${error.message}`)
          }

          // - - - - - - - - - - - - - - - - - - Call the Web Worker - - - - - - - - - - - - - - - - - -
          // Calls the web-worker, evoking the onmessage function defined in the worker file.
          nominalWorker.postMessage(nominalCalculationProps)
        })
      ),

    [assetModel, componentId, forecastComponentConfig]
  )

  // ------------------------------------------------------------------
  // Create a function to generate a nominal GeckoComponentDataFrame object and insert its data into the `cdfDataFetchCache`
  const { putBinnedDataBufferInCache } = useBinnedDataBufferCache()

  const assembleNominalCdf = useCallback(
    (
      binSize: GeckoBinSize,
      nominalDate: Date,
      relevantComponentModel: GeckoComponentModel,
      generationNumber: number,
      cdfTags: GeckoComponentDataFrame['tags']
    ): GeckoComponentDataFrame => {
      const geometryXStart =
        relevantComponentModel.type ===
        GeckoComponentModelType.LegacyBoilerWalls
          ? relevantComponentModel.componentBounds.start.x
          : relevantComponentModel.componentBounds.start.x.minus(
              binSize.width.scale(0.5)
            )

      const geometryYStart =
        relevantComponentModel.componentBounds.start.y.minus(
          binSize.height.scale(0.5)
        )

      const geometryXEnd = relevantComponentModel.componentBounds.end.x

      const geometryYEnd = relevantComponentModel.componentBounds.end.y

      const startX = snapToNearestMultiple(
        geometryXStart.toNumber(binSize.width.unit),
        binSize.width.num / 2,
        SnapType.Floor
      )

      const endX =
        snapToNearestMultiple(
          geometryXEnd.toNumber(binSize.width.unit) - startX,
          binSize.width.num,
          SnapType.Ceil
        ) + startX

      const startY = snapToNearestMultiple(
        geometryYStart.toNumber(binSize.height.unit),
        binSize.height.num / 2,
        SnapType.Floor
      )

      const endY =
        snapToNearestMultiple(
          geometryYEnd.toNumber(binSize.height.unit) - startY,
          binSize.height.num,
          SnapType.Ceil
        ) + startY

      const outputDataXSize = Math.round((endX - startX) / binSize.width.num)

      const outputDataYSize = Math.round((endY - startY) / binSize.height.num)

      const thisId = `nominal-${binSize.id}-${formatDateLikeInspectionData(nominalDate)}-${cdfTags[RolodexEntryType.COMPONENT_DATASET]}`

      // Remove the stats and texture from the data, as they don't carry over

      const cdfEntry = createCdfEntry(thisId, {
        generation: generationNumber,
        geometry: GeckoComponentRect.CreateFromXYs(
          cdfTags.component,
          startX,
          startY,
          endX,
          endY,
          binSize.width.unit,
          binSize.height.unit
        ),
        binning: {
          offset: GeckoComponentCoords.CreateFromValues(
            cdfTags.component,
            0,
            binSize.width.unit,
            0,
            binSize.height.unit
          ),
          bin_size: binSize,
          x_bin_count: outputDataXSize,
          y_bin_count: outputDataYSize,
        },
        // params
        view: GeckoView.Thickness,
        source: GeckoSource.Nominal,
        measurementUnit: NOMINAL_MEASUREMENT_UNIT,
        algorithm: GeckoAlgorithm.None,
        aggregation: GeckoAggregation.None,
        anglePlotSetting: GeckoAnglePlotSetting.None,
        tags: cdfTags,
      })

      const cdfObject = GeckoComponentDataFrame.CreateFromJSON({
        ...cdfEntry,
        view_restriction: null,
      })

      const memoizedGetterFn = memoize(async () =>
        createBinnedDataBufferFromData(
          (
            await calculateNominalValuesInWorker({
              nominalDate,
              outputDataXSize: outputDataXSize,
              outputDataYSize: outputDataYSize,
              binSize,
              geometryXStart: GeckoMeasurement.CreateFromValue(
                startX,
                binSize.width.unit
              ),
              geometryYStart: GeckoMeasurement.CreateFromValue(
                startY,
                binSize.height.unit
              ),
            })
          ).values,
          cdfObject
        )
      )

      putBinnedDataBufferInCache({
        id: thisId,
        generation: generationNumber,
        data: memoizedGetterFn,
      })

      return cdfObject
    },
    [calculateNominalValuesInWorker, putBinnedDataBufferInCache]
  )

  // ------------------------------------------------------------------
  // assemble a nominal CDF for each bin size used in each inspection

  return useMemo(() => {
    const componentModel = assetModel?.data.components.get(componentId ?? '')

    if (
      !componentId ||
      !assetModel ||
      !baseData?.length ||
      !componentModel ||
      !componentHasDefinedNominal(componentModel)
    ) {
      return []
    }

    return baseData.map((curData) =>
      assembleNominalCdf(
        curData.binSize,
        curData.nominalDate,
        componentModel,
        curData.generationNumber,
        curData.cdfEntryTags
      )
    )
  }, [assembleNominalCdf, assetModel, componentId, baseData])
}

// ==================================================================================================================

/**
 * A function that extracts the necessary data to generate a suite of nominal CDFs from the selected entries.
 */
export function getNominalBaseData(
  componentID: string,
  assetModel: GeckoAssetModel,
  componentDatasetsForComponent: ReadonlyArray<GeckoComponentDataset>,
  inspectionsForComponent: ReadonlyArray<GeckoInspection>
): ReadonlyArray<NominalBaseData> {
  const defaultNominalBaseData: NominalBaseData = {
    nominalDate: new Date(),
    binSize: GeckoBinSize.OneInchBins,
    cdfEntryTags: {
      [RolodexEntryType.ORGANIZATION]: assetModel.tags.organization,
      [RolodexEntryType.SITE]: assetModel.tags.site,
      [RolodexEntryType.UNIT]: assetModel.tags.site,
      [RolodexEntryType.COMPONENT]: componentID,
    },
    generationNumber: assetModel.generation,
  }

  if (!componentDatasetsForComponent?.length) {
    return [defaultNominalBaseData]
  }

  return componentDatasetsForComponent.flatMap(
    (cds): NominalBaseData | NominalBaseData[] => {
      const uniqueBinSizes = uniq(
        cds.data.data_frame_settings
          ? Object.values(cds.data.data_frame_settings).map((dfs) =>
              GeckoBinSize.GetOrCreate(
                dfs.x_axis.bin_size,
                dfs.x_axis.measurement_unit,
                dfs.y_axis.bin_size,
                dfs.y_axis.measurement_unit
              )
            )
          : []
      )

      const curCdsCdfEntryTags = {
        ...defaultNominalBaseData.cdfEntryTags,
        [RolodexEntryType.COMPONENT_DATASET]: cds.id,
        [RolodexEntryType.INSPECTION]: cds.tags.inspection,
        [RolodexEntryType.JOB]: cds.tags.job,
      }
      const curNominalGenerationNumbers = assetModel.generation * cds.generation

      if (!uniqueBinSizes.length) {
        return {
          ...defaultNominalBaseData,
          cdfEntryTags: curCdsCdfEntryTags,
          generationNumber: curNominalGenerationNumbers,
        }
      }

      const relevantInspection = inspectionsForComponent.find(
        ({ id }) => id === cds.tags.inspection
      )
      return uniqueBinSizes.map(
        (binSize): NominalBaseData => ({
          nominalDate:
            getInspectionDate({
              inspectionSlug: relevantInspection?.id,
              inspection: relevantInspection,
            })?.toDate() ?? new Date(),
          binSize,
          cdfEntryTags: curCdsCdfEntryTags,
          generationNumber: curNominalGenerationNumbers,
        })
      )
    }
  )
}
